import type { RouteRecordRaw, RouteRecordSingleView } from "vue-router";
import { Section, sections, sectionToSlug } from "@/sections";
import { parseIdUser } from "./params";


declare module "vue-router" {
    interface RouteMeta {
        // Set on the section routes, and only those.
        section?: Section;

        // For screens that are not a section but need one's permission.
        requiresSection?: Section;

        // Reachable without a logged in user.
        public?: boolean;
    }
}

export const sectionRouteName = (section: Section): string =>
    `section:${sectionToSlug(section)}`;

const sectionComponents: Record<Section, RouteRecordSingleView["component"]> = {
    [Section.Overview]: () =>
        import("@/components/user_details/UserDetailsSection.vue"),
    [Section.Access]: () => 
        import("@/components/access/AccessSection.vue"),
    [Section.Licenses]: () => 
        import("@/components/licenses/LicenseSection.vue"),
    [Section.Modules]: () => 
        import("@/components/modules/ModuleSection.vue"),
    [Section.Attributes]: () =>
        import("@/components/attributes/AttributeSection.vue"),
    [Section.Misc]: () => 
        import("@/components/misc/MiscSection.vue"),
    [Section.Customer]: () =>
        import("@/components/customer/CustomerSection.vue"),
};


const toOverview = (idUser: string | string[]) => ({
    name: sectionRouteName(Section.Overview),
    params: { idUser },
});

export const routes: RouteRecordRaw[] = [
    {
        path: "/",
        name: "boot",
        component: () => import("@/views/BlankView.vue"),
    },
    {
        path: "/denied",
        name: "denied",
        component: () => import("@/views/PermissionDeniedView.vue"),
        meta: { public: true },
    },
    {
        path: "/error",
        name: "error",
        component: () => import("@/views/BootErrorView.vue"),
        meta: { public: true },
    },
    {
        // :idUser is deliberately unconstrained: a malformed id should reach a
        // guard, not fall through to the catch-all and lose the section.
        path: "/users/:idUser",
        component: () => import("@/views/UserShell.vue"),
        children: [
            { path: "", redirect: (to) => toOverview(to.params.idUser) },

            // One record per section rather than a single :section param, so
            // each gets its own meta.section and its own chunk.
            ...sections.map((section) => ({
                path: sectionToSlug(section),
                name: sectionRouteName(section),
                component: sectionComponents[section],
                meta: { section },
            })),

            // Literal siblings, so they cannot collide with a section slug.
            {
                path: "new-user",
                name: "new-user",
                component: () => import("@/views/NewUserView.vue"),
            },
            {
                path: "customer/new",
                name: "customer:new",
                component: () =>
                    import("@/components/customer/CustomerChangeNew.vue"),
                meta: { requiresSection: Section.Customer },
            },
            {
                path: "customer/change",
                name: "customer:change",
                component: () =>
                    import("@/components/customer/CustomerChangeExisting.vue"),
                meta: { requiresSection: Section.Customer },
                props: (to) => ({ idUser: parseIdUser(to.params.idUser) }),
            },

            {
                path: ":pathMatch(.*)*",
                redirect: (to) => toOverview(to.params.idUser),
            },
        ],
    },
    { path: "/:pathMatch(.*)*", redirect: "/" },
];